import { STEERFI_PARTY_ID } from '@steerfi/shared';
import { Upload } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getDownloadUrl,
  getPaymentFiles,
  getPaymentUploadConfig,
  type PaginatedPaymentFilesResponse,
  type PaymentFileItem,
  type PaymentUploadConfigResponse,
} from '@/api/file-upload-api';
import { PaymentFileQuickViewDialog } from '@/components/payment-file-quick-view-dialog';
import { PaymentFileUploadModal } from '@/components/payment-file-upload';
import {
  DEFAULT_PAYMENT_FILE_SORT_KEY,
  PaymentFilesTable,
  readPaymentFileSortBy,
} from '@/components/payment-files-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useFocusReturn } from '@/hooks/use-focus-return';

interface PaymentFileFilters {
  party_id?: string;
  status_type_id?: number;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function buildPaymentFilesSearchParams(filters: PaymentFileFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.page && filters.page !== 1) params.set('page', String(filters.page));
  if (filters.limit && filters.limit !== 20) params.set('limit', String(filters.limit));
  if (filters.sortBy && filters.sortBy !== 'created_at') params.set('sortBy', filters.sortBy);
  if (filters.sortOrder && filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder);
  if (filters.party_id) params.set('party_id', filters.party_id);
  if (filters.status_type_id !== undefined)
    params.set('status_type_id', String(filters.status_type_id));
  if (filters.from_date) params.set('from_date', filters.from_date);
  if (filters.to_date) params.set('to_date', filters.to_date);
  return params;
}

export const PaymentFiles: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useKeycloakAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastWrittenQueryRef = useRef<string | null>(null);
  const [filesData, setFilesData] = useState<PaginatedPaymentFilesResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [quickViewFile, setQuickViewFile] = useState<PaymentFileItem | null>(null);
  const { capture: captureFocus, restore: restoreFocus } = useFocusReturn();
  const [uploadConfigLoaded, setUploadConfigLoaded] = useState(false);
  const [uploadConfig, setUploadConfig] = useState<PaymentUploadConfigResponse>({
    allowedUploadTypes: ['ui_batch', 'sftp'],
    supportedUploadTypes: { uiBatch: true, sftp: true },
  });
  const hasLoadedRef = useRef(false);
  const [filters, setFilters] = useState<PaymentFileFilters>(() => {
    const initial: PaymentFileFilters = {
      page: Number(searchParams.get('page') || 1) || 1,
      limit: Number(searchParams.get('limit') || 20) || 20,
      sortBy: readPaymentFileSortBy(searchParams.get('sortBy'), DEFAULT_PAYMENT_FILE_SORT_KEY),
      sortOrder:
        ((searchParams.get('sortOrder') || 'desc').toLowerCase() as 'asc' | 'desc') || 'desc',
    };
    const partyId = searchParams.get('party_id')?.trim();
    if (partyId) initial.party_id = partyId;
    const statusTypeId = Number(searchParams.get('status_type_id'));
    if (Number.isFinite(statusTypeId)) initial.status_type_id = statusTypeId;
    const fromDate = searchParams.get('from_date')?.trim();
    if (fromDate) initial.from_date = fromDate;
    const toDate = searchParams.get('to_date')?.trim();
    if (toDate) initial.to_date = toDate;
    return initial;
  });

  const fetchFiles = useCallback(async (currentFilters: typeof filters) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getPaymentFiles(currentFilters);
      setFilesData(response);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load files';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchFiles(filters);
    }
  }, [fetchFiles, filters]);

  useEffect(() => {
    if (!isAuthenticated || !user?.partyId) {
      setUploadConfigLoaded(true);
      return;
    }

    setUploadConfigLoaded(false);
    getPaymentUploadConfig(user.partyId)
      .then((config) => setUploadConfig(config))
      .catch(() =>
        setUploadConfig({
          allowedUploadTypes: ['ui_batch', 'sftp'],
          supportedUploadTypes: { uiBatch: true, sftp: true },
        })
      )
      .finally(() => setUploadConfigLoaded(true));
  }, [isAuthenticated, user?.partyId]);

  // Compat: legacy links/emails use /payment-files?fileId=...; redirect to the detail subpage.
  useEffect(() => {
    const fileId = searchParams.get('fileId')?.trim();
    if (fileId) {
      navigate(`/payment-files/${fileId}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleFilterChange = (filterKey: string, value: string | string[]) => {
    const newFilters = {
      ...filters,
      [filterKey]: value,
      page: 1, // Reset to first page when filters change
    };
    setFilters(newFilters);
    fetchFiles(newFilters);
  };

  const handleSortChange = (sortBy: string, order: 'asc' | 'desc') => {
    const newFilters = {
      ...filters,
      sortBy,
      sortOrder: order,
    };
    setFilters(newFilters);
    fetchFiles(newFilters);
  };

  const handlePageChange = (page: number) => {
    const newFilters = {
      ...filters,
      page,
    };
    setFilters(newFilters);
    fetchFiles(newFilters);
  };

  const handleFileClick = (file: PaymentFileItem) => {
    navigate(`/payment-files/${file.file_id}`);
  };

  const getFileLink = (file: PaymentFileItem) => ({ to: `/payment-files/${file.file_id}` });

  const handleQuickView = (file: PaymentFileItem) => {
    captureFocus();
    setQuickViewFile(file);
  };

  const handleDownload = async (file: PaymentFileItem) => {
    try {
      const downloadData = await getDownloadUrl(file.file_id);

      // Open signed URL in new tab
      window.open(downloadData.signed_url, '_blank');

      toast.success(`Downloading ${file.file_name}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download file';
      toast.error(errorMessage);
    }
  };

  const handleUploadSuccess = () => {
    // Refresh files list after successful upload
    fetchFiles(filters);
  };

  useEffect(() => {
    const current = searchParams.toString();
    if (lastWrittenQueryRef.current && lastWrittenQueryRef.current === current) {
      lastWrittenQueryRef.current = null;
      return;
    }

    setFilters((prev) => {
      const next: PaymentFileFilters = {
        ...prev,
        page: Number(searchParams.get('page') || 1) || 1,
        limit: Number(searchParams.get('limit') || prev.limit || 20) || prev.limit || 20,
        sortBy: readPaymentFileSortBy(
          searchParams.get('sortBy'),
          prev.sortBy ?? DEFAULT_PAYMENT_FILE_SORT_KEY
        ),
        sortOrder:
          ((searchParams.get('sortOrder') || prev.sortOrder || 'desc').toLowerCase() as
            | 'asc'
            | 'desc') || 'desc',
      };

      const partyId = searchParams.get('party_id')?.trim();
      if (partyId) {
        next.party_id = partyId;
      } else {
        delete next.party_id;
      }

      const statusTypeId = Number(searchParams.get('status_type_id'));
      if (Number.isFinite(statusTypeId)) {
        next.status_type_id = statusTypeId;
      } else {
        delete next.status_type_id;
      }

      const fromDate = searchParams.get('from_date')?.trim();
      if (fromDate) {
        next.from_date = fromDate;
      } else {
        delete next.from_date;
      }
      const toDate = searchParams.get('to_date')?.trim();
      if (toDate) {
        next.to_date = toDate;
      } else {
        delete next.to_date;
      }

      return next;
    });
  }, [searchParams]);

  useEffect(() => {
    const nextParams = buildPaymentFilesSearchParams(filters);
    const next = nextParams.toString();
    const current = searchParams.toString();
    if (next === current) return;
    lastWrittenQueryRef.current = next;
    setSearchParams(nextParams, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold tracking-tight">Payment Files</h1>
            <p className="text-sm text-muted-foreground">View and manage uploaded payment files</p>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pf-from-date" className="text-xs">
                From
              </Label>
              <Input
                id="pf-from-date"
                type="date"
                className="h-9 w-[145px]"
                value={filters.from_date ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const { from_date: _, ...rest } = filters;
                  const next: PaymentFileFilters = { ...rest, page: 1 };
                  if (v) next.from_date = v;
                  setFilters(next);
                  fetchFiles(next);
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pf-to-date" className="text-xs">
                To
              </Label>
              <Input
                id="pf-to-date"
                type="date"
                className="h-9 w-[145px]"
                value={filters.to_date ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const { to_date: _, ...rest } = filters;
                  const next: PaymentFileFilters = { ...rest, page: 1 };
                  if (v) next.to_date = v;
                  setFilters(next);
                  fetchFiles(next);
                }}
              />
            </div>
            {(filters.from_date || filters.to_date) && (
              <button
                type="button"
                className="mb-0.5 text-xs text-muted-foreground underline hover:text-foreground"
                onClick={() => {
                  const next = { ...filters, page: 1 };
                  delete next.from_date;
                  delete next.to_date;
                  setFilters(next);
                  fetchFiles(next);
                }}
              >
                Clear
              </button>
            )}
            {uploadConfigLoaded && uploadConfig.supportedUploadTypes.uiBatch && (
              <Button
                onClick={() => {
                  setIsUploadModalOpen(true);
                }}
                className="gap-2"
                disabled={!isAuthenticated || authLoading}
              >
                <Upload className="h-4 w-4" />
                Upload Payment File
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <PaymentFilesTable
          caption="Payment files"
          data={filesData}
          isLoading={isLoading}
          error={error}
          getRowLink={getFileLink}
          onQuickView={handleQuickView}
          onDownload={handleDownload}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          currentSortKey={filters.sortBy ?? DEFAULT_PAYMENT_FILE_SORT_KEY}
          currentSortOrder={filters.sortOrder ?? 'desc'}
          onPageChange={handlePageChange}
        />
      </div>

      {uploadConfigLoaded && uploadConfig.supportedUploadTypes.uiBatch && (
        <PaymentFileUploadModal
          open={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadSuccess={handleUploadSuccess}
          partyId={user?.partyId || STEERFI_PARTY_ID}
        />
      )}

      <PaymentFileQuickViewDialog
        file={quickViewFile}
        open={quickViewFile !== null}
        onOpenChange={(open) => {
          if (!open) {
            restoreFocus();
            setQuickViewFile(null);
          }
        }}
        onOpenFile={handleFileClick}
        onDownload={handleDownload}
      />
    </div>
  );
};
